import { Box, Button, Snackbar, Tabs, Tab } from "@mui/material";
import PinterestIcon from "@mui/icons-material/Pinterest";
import YouTubeIcon from "@mui/icons-material/YouTube";
import MusicNoteIcon from "@mui/icons-material/MusicNote";
import InstagramIcon from "@mui/icons-material/Instagram";
import TwitterIcon from "@mui/icons-material/Twitter";
import FacebookIcon from "@mui/icons-material/Facebook";
import { config, generateTimeStr, generateUUID } from "../Const";
import { useEffect, useState } from "react";
import FacebookGroup from "./FacebookGroup";
import XPost from "./XPost";
import { executeFetch } from "../Util";
import PinterestPin from "./PinterestPin";
import YouTubeShort from "./YouTubeShort";
import TikTokShort from "./TikTokShort";
import InstagramReel from "./InstagramReel";
import FacebookReel from "./FacebookReel";

function a11yProps(index) {
    return {
        id: `publish-tab-${index}`,
        "aria-controls": `publish-tabpanel-${index}`,
    };
}

/**
 * TabPanel now keeps children mounted at all times.
 * It uses CSS display:none when not active so internal state is preserved.
 */
function TabPanel({ children, value, index, ...other }) {
    const isActive = value === index;
    return (
        <div
            role="tabpanel"
            aria-hidden={!isActive}
            id={`publish-tabpanel-${index}`}
            aria-labelledby={`publish-tab-${index}`}
            style={{ display: isActive ? undefined : "none" }}
            {...other}
        >
            <Box sx={{ pt: 2 }}>{children}</Box>
        </div>
    );
}

export default function Publish({ seoTitle, type, detailedEntity }) {
    // Tabs state — default 0 (Pinterest is the first tab)
    const [tabIndex, setTabIndex] = useState(0);

    // Existing state (kept)
    const [markPublishedSnackbarOpen, setMarkPublishedSnackbarOpen] = useState(false);
    const pageUrl = `${config.APP_URL_HOST}/${type.toLowerCase()}/${seoTitle}`;
    const [facebookGroups, setFacebookGroups] = useState([]);
    const [publishedTokenList, setPublishedTokenList] = useState([]);
    const [xPageUrl, setXPageUrl] = useState("");
    const [pinterestUrl, setPinterestUrl] = useState("");

    // Handlers for Tabs
    const handleChangeTab = (event, newValue) => {
        setTabIndex(newValue);
    };

    // Helpers (kept/ported from your original)
    const generatefbUrl = (groupId) => {
        return `${pageUrl}?t=f${groupId}${generateTimeStr()}`;
    };

    const handleCopyClick = (url) => {
        navigator.clipboard.writeText(url);
    };

    const handleFacebookGroupChange = (id, groupId) => {
        const fbUrl = generatefbUrl(groupId);
        const updatedFg = { id: id, groupId: groupId, url: fbUrl };
        setFacebookGroups(facebookGroups.map((fg) => (fg.id === id ? updatedFg : fg)));
    };

    const generateFBGroup = () => {
        return { id: generateUUID() };
    };

    const handleAddGroup = () => {
        const fbGroup = generateFBGroup();
        setFacebookGroups([...facebookGroups, fbGroup]);
    };

    const isMarkPublishedButtonDisabled = (url) => {
        if (url) {
            const token = url.substring(url.indexOf("?t=") + 3);
            return publishedTokenList.some((obj) => obj.token === token);
        } else {
            return false;
        }
    };

    const handleMarkPublishedClick = (url) => {
        const publishToken = url.replace(pageUrl + "?t=", "");

        const formData = new FormData();
        formData.append("type", type);
        formData.append("seoTitle", seoTitle);
        formData.append("token", publishToken);

        const apiUrl = `${config.SERVER_URL}/api/v1/admin/quiz/markPublished`;

        const responseDataHandler = (result) => {
            setMarkPublishedSnackbarOpen(true);
            setPublishedTokenList([...publishedTokenList, { token: publishToken, publishDate: result.publishDate }]);
        };

        executeFetch(apiUrl, "post", null, formData, null, responseDataHandler);
    };

    const handleDeletePublishFBClick = (id) => {
        const fbGroup = facebookGroups.filter((fg) => fg.id === id)[0];
        const url = fbGroup.url;

        handleDeletePublishClick(url);

        setFacebookGroups(facebookGroups.filter((fg) => fg.id !== id));
    };

    const handleDeleteXClick = (url) => {
        handleDeletePublishClick(url);
        setXPageUrl(`${pageUrl}?t=x${generateTimeStr()}`);
    };



    const handleDeletePublishClick = (url) => {
        const publishToken = url.replace(pageUrl + "?t=", "");
        const isPublished = publishedTokenList.some((pt) => pt.token === publishToken);

        // Only if this is a saved PagePublish, send an API call to delete it
        if (isPublished) {
            const formData = new FormData();
            formData.append("token", publishToken);

            const apiUrl = `${config.SERVER_URL}/api/v1/admin/quiz/deletePublished`;

            const responseDataHandler = (result) => {
                // optional: remove from local publishedTokenList (keep UI consistent)
                setPublishedTokenList((prev) => prev.filter((pt) => pt.token !== publishToken));
            };

            executeFetch(apiUrl, "post", null, formData, null, responseDataHandler);
        }
    };

    const handleSnackbarClose = (event, reason) => {
        if (reason === "clickaway") {
            return;
        }
        setMarkPublishedSnackbarOpen(false);
    };

    const getPublishDateFromUrl = (url) => {
        let publishToken = url?.replace(pageUrl + "?t=", "");
        let publishDate = publishedTokenList.find((pt) => pt.token === publishToken)?.publishDate;
        return publishDate;
    };

    // Load published tokens / populate X / FB / Pinterest URLs (kept logic with small enhancements)
    useEffect(() => {
        const apiUrl = `${config.SERVER_URL}/api/v1/admin/quiz/pagePublishList?type=${type}&seoTitle=${seoTitle}`;

        const responseDataHandler = (result) => {
            setPublishedTokenList(result.map((p) => ({ token: p.token, publishDate: p.publishDate })));

            // X
            let xPagePublish = result.filter((p) => p.token.charAt(0) === "x")[0] || null;
            if (xPagePublish) {
                let token = xPagePublish.token.substring(1);
                setXPageUrl(`${pageUrl}?t=x${token}`);
            } else {
                setXPageUrl(`${pageUrl}?t=x${generateTimeStr()}`);
            }

            // Pinterest (new) — token prefix 'p'
            let pPagePublish = result.filter((p) => p.token.charAt(0) === "p")[0] || null;
            if (pPagePublish) {
                let token = pPagePublish.token.substring(1);
                setPinterestUrl(`${pageUrl}?t=p${token}`);
            } else {
                setPinterestUrl(`${pageUrl}?t=p${generateTimeStr()}`);
            }

            // Facebook groups
            setFacebookGroups(
                result
                    .filter((p) => p.token.charAt(0) === "f")
                    .map((p) => {
                        // original parsing used last 8 chars as time part — keep that assumption
                        const groupId = p.token.substring(1, p.token.length - 8);
                        const timePart = p.token.substring(p.token.length - 8, p.token.length);

                        return {
                            id: p.id,
                            groupId: groupId,
                            url: `${pageUrl}?t=f${groupId}${timePart}`,
                            publishDate: p.publishDate,
                        };
                    })
            );
        };

        executeFetch(apiUrl, "get", null, null, null, responseDataHandler);
    }, [type, seoTitle, pageUrl]);

    // favicon URL used for QuizMyDay tab icon
    const faviconUrl = "https://music.quizmyday.com/favicon.ico";

    const QuizFavicon = () => (
        <Box component="img" src={faviconUrl} alt="QuizMyDay icon" sx={{ width: 18, height: 18, objectFit: "contain", display: "inline-block" }} />
    );

    return (
        <Box sx={{ minHeight: "60vh" }}>
            <Tabs variant="fullWidth" value={tabIndex} onChange={handleChangeTab} aria-label="publish tabs">
                <Tab
                    {...a11yProps(0)}
                    label={
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <PinterestIcon fontSize="small" />
                            <Box>Pinterest</Box>
                        </Box>
                    }
                />
                <Tab
                    {...a11yProps(1)}
                    label={
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <YouTubeIcon fontSize="small" />
                            <Box>YouTube</Box>
                        </Box>
                    }
                />
                <Tab
                    {...a11yProps(2)}
                    label={
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <MusicNoteIcon fontSize="small" />
                            <Box>TikTok</Box>
                        </Box>
                    }
                />
                <Tab
                    {...a11yProps(3)}
                    label={
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <InstagramIcon fontSize="small" />
                            <Box>Instagram</Box>
                        </Box>
                    }
                />
                <Tab
                    {...a11yProps(4)}
                    label={
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <FacebookIcon fontSize="small" />
                            <Box>FB Reel</Box>
                        </Box>
                    }
                />
                <Tab
                    {...a11yProps(5)}
                    label={
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <QuizFavicon />
                            <Box>QuizMyDay</Box>
                        </Box>
                    }
                />
                <Tab
                    {...a11yProps(6)}
                    label={
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <TwitterIcon fontSize="small" />
                            <Box>X</Box>
                        </Box>
                    }
                />
                <Tab
                    {...a11yProps(7)}
                    label={
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <FacebookIcon fontSize="small" />
                            <Box>FB Groups</Box>
                        </Box>
                    }
                />
            </Tabs>

            {/* Tab 0 — Pinterest */}
            <TabPanel value={tabIndex} index={0}>
                <Box sx={{ mt: 2, fontWeight: 700, mb: 1 }}>Pinterest Pin</Box>

                <Box sx={{ mt: 5, display: "flex", gap: "20px", alignItems: "center" }}>
                    <PinterestPin
                        detailedEntity={detailedEntity}
                        pinterestUrl={pinterestUrl}
                        pageUrl={pageUrl}
                        publishedTokenList={publishedTokenList}
                    />
                </Box>
            </TabPanel>

            {/* Tab 1 — YouTube Shorts */}
            <TabPanel value={tabIndex} index={1}>
                <Box sx={{ mt: 2, fontWeight: 700, mb: 2 }}>YouTube Short</Box>
                <YouTubeShort seoTitle={seoTitle} type={type} detailedEntity={detailedEntity} />
            </TabPanel>

            <TabPanel value={tabIndex} index={2}>
                <Box sx={{ mt: 2, fontWeight: 700, mb: 2 }}>TikTok video</Box>
                <TikTokShort seoTitle={seoTitle} type={type} />
            </TabPanel>

            <TabPanel value={tabIndex} index={3}>
                <Box sx={{ mt: 2, fontWeight: 700, mb: 2 }}>Instagram Reel</Box>
                <InstagramReel seoTitle={seoTitle} type={type} />
            </TabPanel>

            {/* Tab 2 — QuizMyDay (general URL) */}
            <TabPanel value={tabIndex} index={4}>
                <Box sx={{ mt: 2, fontWeight: 700, mb: 2 }}>Facebook Reel</Box>
                <FacebookReel seoTitle={seoTitle} type={type} />
            </TabPanel>

            <TabPanel value={tabIndex} index={5}>
                <Box>
                    <Box sx={{ fontWeight: 700 }}>Quiz URL</Box>

                    {seoTitle && (
                        <Box sx={{ display: "flex", alignItems: "center", gap: "20px", mt: 1 }}>
                            <Box>{pageUrl}</Box>
                            <Button size={"small"} variant="outlined" onClick={() => handleCopyClick(pageUrl)}>
                                Copy
                            </Button>
                        </Box>
                    )}
                </Box>
            </TabPanel>

            {/* Tab 3 — X */}
            <TabPanel value={tabIndex} index={6}>
                <Box sx={{ fontWeight: 700, mb: 1 }}>X Post URL</Box>
                <Box sx={{ display: "flex", gap: "20px", alignItems: "center" }}>
                    <Box sx={{ width: "606px" }}>
                        <XPost url={xPageUrl} />
                    </Box>
                    <Box sx={{ display: "flex", gap: "10px", fontSize: "0.9rem", alignItems: "center" }}>
                        <Button size={"small"} variant="outlined" onClick={() => handleCopyClick(xPageUrl)}>
                            Copy
                        </Button>
                        <Button
                            size={"small"}
                            sx={{ minWidth: "150px" }}
                            disabled={isMarkPublishedButtonDisabled(xPageUrl)}
                            variant="contained"
                            onClick={() => handleMarkPublishedClick(xPageUrl)}
                        >
                            Mark Published
                        </Button>

                        <Box sx={{ width: "200px" }}>
                            {(() => {
                                let publishDate = getPublishDateFromUrl(xPageUrl);
                                let publishDateFormatted = publishDate ? new Date(publishDate).toLocaleString() : "";
                                return publishDateFormatted;
                            })()}
                        </Box>

                        <Button
                            color={"error"}
                            size={"small"}
                            variant="contained"
                            disabled={!isMarkPublishedButtonDisabled(xPageUrl)}
                            onClick={() => handleDeleteXClick(xPageUrl)}
                        >
                            Delete
                        </Button>
                    </Box>
                </Box>
            </TabPanel>

            {/* Tab 4 — Facebook */}
            <TabPanel value={tabIndex} index={7}>
                <Box sx={{ display: "flex", gap: "40px", width: "100%", alignItems: "center" }}>
                    <Box sx={{ fontWeight: 700 }}>Facebook Posts</Box>
                    <Button size={"small"} variant="contained" onClick={handleAddGroup}>
                        Add Group
                    </Button>
                </Box>

                <Box sx={{ mt: 2 }}>
                    {facebookGroups.map((fg) => {
                        let fgUrl = fg.url;
                        let publishDate = getPublishDateFromUrl(fgUrl);
                        let publishDateFormatted = publishDate ? new Date(publishDate).toLocaleString() : "";

                        return (
                            <Box
                                key={fg.id}
                                sx={{ display: "flex", gap: "20px", mt: 2, mb: 2, width: "800px", alignItems: "center" }}
                            >
                                <FacebookGroup
                                    fg={fg}
                                    disabled={isMarkPublishedButtonDisabled(fg.url)}
                                    onChange={(value) => handleFacebookGroupChange(fg?.id, value)}
                                />

                                <Box sx={{ display: "flex", gap: "10px", alignItems: "center" }}>
                                    <Button size={"small"} variant="outlined" onClick={() => handleCopyClick(fg.url)}>
                                        Copy
                                    </Button>
                                    <Button
                                        sx={{ minWidth: "150px" }}
                                        size={"small"}
                                        disabled={isMarkPublishedButtonDisabled(fg.url)}
                                        variant="contained"
                                        onClick={() => handleMarkPublishedClick(fg.url)}
                                    >
                                        Mark Published
                                    </Button>

                                    <Box sx={{ width: "200px", fontSize: "0.9rem" }}>
                                        {publishDate && <Box>{publishDateFormatted}</Box>}
                                    </Box>
                                    <Button
                                        color={"error"}
                                        size={"small"}
                                        variant="contained"
                                        disabled={!isMarkPublishedButtonDisabled(fg.url)}
                                        onClick={() => handleDeletePublishFBClick(fg.id)}
                                    >
                                        Delete
                                    </Button>
                                </Box>
                            </Box>
                        );
                    })}
                </Box>
            </TabPanel>

            <Snackbar open={markPublishedSnackbarOpen} autoHideDuration={3000} message="Page Marked as published" onClose={handleSnackbarClose} />
        </Box>
    );
}
